'use client'

import { useEffect, useRef, useState } from 'react'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { GlobeAltIcon } from '@heroicons/react/20/solid'
import { useLocale } from 'next-intl'
//import { getLocale, getMessages } from 'next-intl/server';
// @@I18N
import { useRouter, usePathname } from '@/i18n/navigation'
import { getCookie, setCookie } from 'cookies-next'
import logWrapper from '@/utils/logWrapper'
import CheckmarkWrapper from '@/components/common/CheckmarkWrapper'

// @@TODO: Switching language flashes font.

type language = {
  name: string
  locale?: 'en' | 'de' | 'nl' | 'ar' // @@TODO: Quick workaround for string -> type error
  // Type error: Type 'string' is not assignable to type '"en" | "de" | "nl" | "ar" | undefined'.
}

const languages: Array<language> = [
  { name: 'English', locale: 'en' },
  { name: 'Deutsch', locale: 'de' },
  { name: 'Nederlands', locale: 'nl' },
  { name: 'عربي', locale: 'ar' },
]

// @@TODO: Should be consistent with defaultLocale in next.config.js
const defaultLanguage = languages[0] // @@TODO: Named index

const getLanguageFromLocale = (locale: string) => {
  const cookie_locale = getCookie('NEXT_LOCALE')
  logWrapper('getLanguageFromLocale() - cookie_locale=', { cookie_locale })

  // @@TODO: Use defined default locale (not defaultLanguage)?
  const language = languages.find((e) => e.locale === locale) ?? defaultLanguage
  logWrapper('getLanguageFromLocale() - language=', language)
  return language
}

const LanguageSelect = () => {
  const router = useRouter()
  const pathname = usePathname()
  // @@TODO
  const locale = useLocale()
  //const locale = await getLocale()
  const defaultLocale = defaultLanguage.locale

  console.log('locale=', { locale })
  logWrapper('locale=', { locale })
  //logWrapper('locales=', locales)
  logWrapper('defaultLocale=', { defaultLocale })
  const [selected, setSelected] = useState(
    getLanguageFromLocale(locale ?? defaultLocale ?? defaultLanguage.locale),
  )

  const changeLanguage = (selected: language) => {
    console.log('change language=', selected.locale)
    // NOTE: Call async function without awaiting; safe to ignore the promise in this event handler context
    // NOTE: Intentionally not awaited.
    void setCookie('NEXT_LOCALE', selected.locale) // NOSONAR
    setSelected(selected)
    router.push(pathname, { locale: selected.locale })
    router.refresh()
  }

  const toggleLanguage = () => {
    let newSelected
    switch (selected.locale) {
      case 'en':
        newSelected = languages[1]
        break
      case 'de':
        newSelected = languages[2]
        break
      case 'nl':
        newSelected = languages[3]
        break
      case 'ar':
      default: // Deliberate fall-through
        newSelected = languages[0]
        break
    }
    // NOTE: Call async function without awaiting; safe to ignore the promise in this event handler context
    // NOTE: Intentionally not awaited.
    void setCookie('NEXT_LOCALE', newSelected.locale) // NOSONAR
    setSelected(newSelected)
    router.push(pathname, { locale: newSelected.locale })
  }

  // Ref for the event handler function to maintain identity between renders
  const handlerRef = useRef<((event: KeyboardEvent) => void) | null>(null)

  useEffect(function setKeyHandler() {
    // NOTE: Call async function without awaiting; safe to ignore the promise in this event handler context
    // @@TODO: Is this an async function?
    handlerRef.current = (event: KeyboardEvent): void => {
      console.log('toggleOptionKey() - a key pressed')
      if (event.isComposing || event.key === 'Meta') {
        console.log('toggleOptionKey() - command(meta) key pressed')
        // @@TODO
        //toggleLanguage()
      }
    }
  }, [])

  useEffect(function setupListeners() {
    // Prevent adding unlimited event listeners (due to hot reload in dev mode).
    const listener = (event: KeyboardEvent) => handlerRef.current?.(event)
    window.addEventListener('keydown', listener) // NOSONAR
    return function cleanupListeners() {
      window.removeEventListener('keydown', listener) // NOSONAR
    }
  }, [])

  return (
    <div>
      <Listbox
        value={selected}
        onChange={(selected) => {
          changeLanguage(selected)
        }}
      >
        {/* NOTE: Cannot use w-fit because the widest element of the list must fit. */}
        <ListboxButton className="focus-custom flex w-28 h-7 rounded-md justify-center items-center border-custom">
          <div className="truncate">{selected.name}</div>
          <div className="px-1"></div>
          <GlobeAltIcon className="h-5 w-5" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          className="absolute w-fill rounded-md shadow-md shadow-violet-600 dark:shadow-violet-600 bg-background-light text-foreground-light dark:bg-background-dark dark:text-foreground-dark
          data-enter:transition data-enter:ease-out data-enter:duration-100
          data-enter-[from]:transform data-enter-[from]:opacity-0 data-enter-[from]:scale-95
          data-enter-[to]:transform data-enter-[to]:opacity-1000 data-enter-[to]:scale-100
          data-leave:transition data-leave:ease-in data-leave:duration-75
          data-leave-[from]:transform data-leave-[from]:opacity-100 data-leave-[from]:scale-100
          data-leave-[to]:transform data-leave-[to]:opacity-0 data-leave-[to]:scale-95"
          modal={false}
          transition
        >
          {languages.map((language) => (
            <div className="px-1 first:mt-1 last:mb-1" key={language.name}>
              <ListboxOption
                className="cursor-default select-none pe-8 group flex w-full items-center rounded-md px-2 py-2 data-focus:bg-gray-200 dark:data-focus:bg-gray-700"
                value={language}
              >
                <CheckmarkWrapper text={language.name} />
              </ListboxOption>
            </div>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}

export default LanguageSelect
